import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

/**
 * SuperAdminThemeContext - Isolated theme management for Governance UI
 * 
 * ISOLATION REQUIREMENTS:
 * - Uses separate localStorage key: 'superadmin_theme'
 * - Does NOT affect CRM UI theme
 * - CRM theme does NOT read this value
 * - Default theme: 'dark' (security-focused admin interface)
 */

const SuperAdminThemeContext = createContext(null);

const STORAGE_KEY = 'superadmin_theme';
const DEFAULT_THEME = 'dark';

// Track if any SuperAdminThemeProvider is mounted
let providerMountCount = 0;

export const SuperAdminThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to dark
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    }
    return DEFAULT_THEME;
  });

  // Apply theme to document when component mounts and theme changes
  useEffect(() => {
    providerMountCount++;
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, theme);
    
    // Apply dark class to document element for CSS variable inheritance
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Log for debugging
    console.log('[SuperAdminTheme] Theme set to:', theme, 'Provider count:', providerMountCount);
    
    // Cleanup: only remove dark class when ALL providers are unmounted (truly leaving governance)
    return () => {
      providerMountCount--;
      if (providerMountCount === 0) {
        document.documentElement.classList.remove('dark');
        console.log('[SuperAdminTheme] All providers unmounted, removing dark class');
      }
    };
  }, [theme]);

  // Toggle between dark and light
  const toggleTheme = useCallback(() => {
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  // Set specific theme
  const setTheme = useCallback((newTheme) => {
    if (newTheme === 'dark' || newTheme === 'light') {
      setThemeState(newTheme);
    }
  }, []);

  const value = {
    theme,
    toggleTheme,
    setTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };

  return (
    <SuperAdminThemeContext.Provider value={value}>
      {children}
    </SuperAdminThemeContext.Provider>
  );
};

export const useSuperAdminTheme = () => {
  const context = useContext(SuperAdminThemeContext);
  if (!context) {
    throw new Error('useSuperAdminTheme must be used within a SuperAdminThemeProvider');
  }
  return context;
};

export default SuperAdminThemeContext;
